import type { RecordEntityId } from '../records';
import type { Site } from '../site';
// TODO: Import types from SDK

export interface DiscountMethod {
  name: 'FIXED_PRICE' | 'AMOUNT_OFF' | 'PERCENT_OFF' | 'FREE';
  value: number;
}

export interface GetDiscountResponse {
  readOnly?: boolean;
  discount: {
    id: string;
    createdAt: string;
    updatedAt: string;
    createdBy: string;
    updatedBy: string;
    name: string;
    type?: string;
    method?: DiscountMethod;
    channelConfig: ChannelConfig;
    is: DiscountIs;
    status: { active: boolean } & DiscountStatus;
    meta: {
      provider: {
        voucherify: {
          discount: DiscountMeta;
        };
      };
    };
    schedule: {
      dailyConfig: {
        mon: DayOfWeekConfig;
        tue: DayOfWeekConfig;
        wed: DayOfWeekConfig;
        thu: DayOfWeekConfig;
        fri: DayOfWeekConfig;
        sat: DayOfWeekConfig;
        sun: DayOfWeekConfig;
      };
      startDate?: string;
      endDate?: string;
    };
    itemLevelConfig?: ItemLevelConfig;
    sites?: DiscountSite[];
    brands?: DiscountBrand[];
  };
}

export interface SiteOption {
  id: RecordEntityId;
  name: string;
}

export interface ChannelConfig {
  thrive: boolean;
  boost: boolean;
  genius: boolean;
  agilysys: boolean;
  volante: boolean;
}

export interface PublishDiscountResponse {
  meta: DiscountMeta;
  sites: DiscountSite[];
}
export interface PutDiscountResponse {
  discount: Discount;
  sites: string[];
}

export interface DeleteDiscountResponse {
  success: boolean;
}
export interface DiscountsResponse {
  discounts: DiscountWithEntities[];
  meta?: {
    resultsCount: number;
    totalPages: number;
    totalResults: number;
    limit: number;
    page: number;
    sortBy?: string;
    sortOrder?: string;
  };
}

export interface DiscountWithEntities {
  discount: {
    id: string;
    createdAt: string;
    updatedAt: string;
    createdBy: string | { first: string; last: string };
    updatedBy: string | { first: string; last: string };
    name: string;
    method?: DiscountMethod;
    is: DiscountIs;
    status: DiscountStatus & { active: boolean };
    meta: {
      provider: {
        voucherify: {
          discount: DiscountMeta;
        };
      };
    };
    schedule: {
      dailyConfig: {
        mon: DayOfWeekConfig;
        tue: DayOfWeekConfig;
        wed: DayOfWeekConfig;
        thu: DayOfWeekConfig;
        fri: DayOfWeekConfig;
        sat: DayOfWeekConfig;
        sun: DayOfWeekConfig;
      };
      startDate?: string;
      endDate?: string;
    };
  };
  sites: DiscountSite[];
  brands: DiscountBrand[];
}

export interface DayOfWeekConfig {
  active: boolean;
  startTime: string;
  endTime: string;
}

export interface DatatableDiscount {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  updatedBy: string;
  name: string;
  active: boolean;
  is: DiscountIs;
  status: DiscountStatuses;
  value: string;
  type?: string;
  sites: string | string[];
  brands: string | string[];
  scheduleStart: Date | string;
  scheduleEnd: Date | string;
}

export interface Discount {
  id?: string;
  readOnly?: boolean;
  active?: boolean;
  status?: DiscountStatus;
  name: string;
  type?: string;
  value?: number;
  method?: string;
  assign_to?: string;
  associations?: DiscountSite[];
  channelConfig?: ChannelConfig;
  is?: DiscountIs;
  meta?: DiscountMeta;
  schedule?: {
    start_date?: Date;
    end_date?: Date;
    start_time?: Date;
    end_time?: Date;
    days?: string[];
    hours?: string;
  };
  itemLevelConfig?: ItemLevelConfig;
  // Additional properties for columns handled by BE
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface Item {
  id: string;
  name: string;
}

export interface ItemLevelCategory {
  id: string;
  name: string;
  items?: Item[];
}

export interface ItemLevelMenu {
  id: string;
  name: string;
  categories?: ItemLevelCategory[];
}

export interface ItemLevelStation {
  id: string;
  name: string;
  menus?: ItemLevelMenu[];
}

export interface ItemLevelSite {
  id: string;
  name: string;
  stations?: ItemLevelStation[];
}

export interface ItemLevelMenuBrand {
  id: string;
  name: string;
  menus?: ItemLevelMenu[];
}

export interface GlobalMenuGroup {
  id: string;
  name: string;
  menuBrands: FetchStationWithMenusResponseDTO[];
}

export interface ItemLevelConfigDetails {
  bundleGroupId?: string;
  bundleGroupName?: string;
  minimum: number;
  sites?: FetchSitesWithStationsResponseDTO[];
  globalMenuGroups?: GlobalMenuGroup[];
}

export interface BundleGroup {
  id: string;
  name: string | null;
  minimumQuantity: number;
  sites?: FetchSitesWithStationsResponseDTO[];
  globalMenuGroups?: GlobalMenuGroup[];
}

export interface ItemLevelConfig {
  bundlePriority?: number;
  sectorName?: string;
  details: ItemLevelConfigDetails[];
}

export interface PutDiscountRequestPayload {
  id?: string;
  name?: string;
  active?: boolean;
  status?: DiscountStatus;
  associations?: DiscountSite[];
  type?: DiscountTypes;
  method?: DiscountMethod;
  channelConfig?: ChannelConfig;
  is?: DiscountIs;
  meta?: DiscountMeta;
  schedule?: {
    startDate?: string;
    endDate?: string;
    startTime?: string;
    endTime?: string;
    days?: string[];
  };
  itemLevelConfig?: ItemLevelConfig;
}

export interface SiteAdditionFormFields {
  site: Site;
}

export interface DiscountBrand {
  id: RecordEntityId;
  name: string;
  channels?: string[];
  meta?: DiscountBrandMeta;
}
export interface DiscountSiteMeta {
  appName: string;
  brandsOfSite: DiscountBrand[];
}

export interface DiscountBrandMeta {
  appName?: string;
  siteOfBrand?: Pick<DiscountSite, 'id' | 'name'>;
  channels?: string[];
}

export interface DiscountSite {
  id: RecordEntityId;
  name: string;
  meta: DiscountSiteMeta;
}

export interface DiscountStatus {
  live?: boolean;
  publishedTo3rdParty?: boolean;
  readyToPublish?: boolean;
  scheduled?: boolean;
  expired?: boolean;
  incomplete?: boolean;
}

export interface DiscountIs {
  badgepayPromo?: boolean;
  mealplanPromo?: boolean;
}

export interface DiscountMeta {
  type: string;
  amountOff?: number;
  percentOff?: number;
  fixedPrice?: number;
}

export interface DiscountSiteStation {
  siteId: string;
  stationIds: DiscountStation[];
}
export interface DiscountStation {
  id: string;
  name: string;
  meta?: {
    channels?: string[];
  };
}

// Enum for column keys
export enum DiscountColumnKeys {
  NAME = 'name',
  STATUS = 'status',
  ACTIVE = 'active',
  SCHEDULE_START = 'scheduleStart',
  SCHEDULE_END = 'scheduleEnd',
  SITES = 'sites',
  BRANDS = 'brands',
  VALUE = 'value',
  TYPE = 'type',
  CREATED = 'created',
  CREATED_BY = 'createdBy',
  UPDATED = 'updated',
  UPDATED_BY = 'updatedBy',
}

export enum DiscountViews {
  LIVE = 'live',
  EXPIRED = 'expired',
  DRAFT = 'draft',
  INCOMPLETE = 'incomplete',
  READY = 'readyToPublish',
  SCHEDULED = 'scheduled',
}

export enum DiscountStatuses {
  LIVE = 'Live',
  SCHEDULED = 'Scheduled',
  READY = 'Ready to Publish',
  INCOMPLETE = 'Incomplete',
  EXPIRED = 'Expired',
}

export enum DiscountTypes {
  TOTAL_ORDER = 'Total Order',
  SINGLE_ITEMS = 'Single Items',
  BUNDLE = 'Bundle',
  BUY_X_GET_Y = 'Buy X Get Y',
  BUY_X_GET_X = 'Buy X Get X',
}

export interface FetchBrandsMetaDTO {
  resultCount?: number;
}

export interface MenuEntityBaseDTO {
  id: string;
  name: string;
  selected?: boolean;
}

export interface FetchBrandMenuDTO {
  id: string;
  name: string;
  categories: FetchStationCategoryResponseDTO[];
}

export interface FetchBrandWithMenusDTO {
  id: string;
  name: string;
  menus: FetchBrandMenuDTO[];
}

export interface FetchBrandsRequestDTO {
  brand_ids: string[];
}

export interface FetchBrandsResponseDTO {
  meta: FetchBrandsMetaDTO;
  results: FetchBrandWithMenusDTO[];
}

export interface FetchStationCategoryResponseDTO {
  id: string;
  name: string;
  items: MenuEntityBaseDTO[];
}

export interface FetchStationMenuResponseDTO {
  id: string;
  name: string;
  categories: FetchStationCategoryResponseDTO[];
}

export interface FetchStationWithMenusResponseDTO {
  id: string;
  name: string;
  menus: FetchStationMenuResponseDTO[];
}

export interface FetchSitesWithStationsRequestDTO {
  id: string;
  name: string;
  stations: FetchBrandWithMenusDTO[];
}

export interface FetchSitesWithStationsResponseDTO {
  id: string;
  name: string;
  stations: FetchStationWithMenusResponseDTO[];
}

export interface SitesBatchGroupBrand {
  id: string;
  name: string;
  channels?: string[];
}

export interface SitesBatchGroupLocation {
  brands: SitesBatchGroupBrand[];
}

export interface SitesBatchGroup {
  id: string;
  name: string;
  locations: SitesBatchGroupLocation[];
}

export interface SitesBatchResponse {
  groups: SitesBatchGroup[];
}

export interface FetchStationItemsRequestDTO {
  sites: FetchSitesWithStationsRequestDTO[];
  searchTerm?: string;
}

export interface FetchStationItemsResponseDTO {
  meta: FetchBrandsMetaDTO;
  results: FetchSitesWithStationsResponseDTO[];
}

export interface FetchGlobalMenuItemsCategoryRequestDTO {
  id: string;
  name: string;
}

export interface FetchGlobalMenuItemsMenuRequestDTO {
  id: string;
  name: string;
  categories?: FetchGlobalMenuItemsCategoryRequestDTO[];
}

export interface FetchGlobalMenuItemsBrandRequestDTO {
  id: string;
  name: string;
  menus?: FetchGlobalMenuItemsMenuRequestDTO[];
}

export interface FetchGlobalMenuItemsRequestDTO {
  menuBrands: FetchGlobalMenuItemsBrandRequestDTO[];
  searchTerm?: string;
}

export interface FetchGlobalMenuItemsCategoryResponseDTO {
  id: string;
  name: string;
  items: MenuEntityBaseDTO[];
}

export interface FetchGlobalMenuItemsMenuResponseDTO {
  id: string;
  name: string;
  categories: FetchGlobalMenuItemsCategoryResponseDTO[];
}

export interface FetchGlobalMenuItemsBrandResponseDTO {
  id: string;
  name: string;
  menus: FetchGlobalMenuItemsMenuResponseDTO[];
}

export interface FetchGlobalMenuItemsResponseDTO {
  meta: FetchBrandsMetaDTO;
  results: FetchGlobalMenuItemsBrandResponseDTO[];
}
